/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Internal dependencies
 */
import { NONE, ITALIC, NORMAL } from '../customConstants';
import {
  getPrefixStylesInSelection,
  setExclusiveInlineStyle,
} from '../styleManipulation';

function elementToStyle(element) {
  const isSpan = element.tagName.toLowerCase() === 'span';
  if (!isSpan) {
    return null;
  }

  if (element.style.fontStyle === 'italic') {
    return ITALIC;
  }
  if (element.style.fontStyle === 'normal') {
    return NORMAL;
  }

  return null;
}

function stylesToCSS(styles) {
  const hasItalic = styles.includes(ITALIC);
  if (hasItalic) {
    return { fontStyle: 'italic' };
  }
  return styles.includes(NORMAL) ? { fontStyle: 'normal' } : null;
}

function isItalic(editorState, context) {
  const italicStyles = getPrefixStylesInSelection(editorState, ITALIC);
  const normalStyles = getPrefixStylesInSelection(editorState, NORMAL);
  if (!normalStyles.includes(NONE) || normalStyles.length > 1) {
    return false;
  }
  return italicStyles.includes(NONE)
    ? context?.defaultFontStyle === 'italic'
    : true;
}

function toggleItalic(editorState, flag, defaultFontStyle = 'normal') {
  const shouldBeItalic =
    typeof flag === 'boolean'
      ? flag
      : !isItalic(editorState, { defaultFontStyle });
  const defaultIsItalic = defaultFontStyle === 'italic';
  const styleToSet =
    shouldBeItalic === defaultIsItalic
      ? null
      : shouldBeItalic
      ? ITALIC
      : NORMAL;

  return setExclusiveInlineStyle(editorState, [ITALIC, NORMAL], styleToSet);
}

const formatter = {
  elementToStyle,
  stylesToCSS,
  autoFocus: true,
  getters: {
    isItalic,
  },
  setters: {
    toggleItalic,
  },
};

export default formatter;
